import { create } from "apisauce";
import ResponseCode from "./ResponseCode";
import { ENVIRONMENT, TOKEN_TYPE } from "../utils/Constant";

const BaseApi = create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    apikey: process.env.REACT_APP_API_KEY,
    Company: process.env.REACT_APP_COMPANY,
  },
  timeout: Number(process.env.REACT_APP_TIME_OUT),
  withCredentials: true,
});

/**
 * Transformando el response para poner los errores generales
 *
 * @param response
 */
function transformResponse(response) {
  if (response.ok) {
    return;
  }

  if (ResponseCode[response.problem]) {
    response.data = {
      problem: ResponseCode[response.problem],
      originalError: response.originalError,
    };
    return;
  }

  response.data = {
    problem: ResponseCode.CONNECTION_ERROR,
    originalError: response.originalError,
  };
}

/**
 * Obtener el token de autenticación desde el store de Redux
 */
const getAuthToken = () => {
  try {
    // Intentar obtener del localStorage (store persistente)
    const persistedState = localStorage.getItem("persist:root");
    if (persistedState) {
      const parsedState = JSON.parse(persistedState);
      if (parsedState.auth) {
        const authState = JSON.parse(parsedState.auth);
        const authUserData = authState?.authUserData;

        if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
          console.log("🔍 AuthUserData:", authUserData);
        }

        // Intentar obtener token en este orden de prioridad:
        // 1. tokenStandVirtual (para usuarios Stand Virtual)
        // 2. Token desde cookies (accessToken)
        // 3. Otros tokens posibles
        if (authUserData?.tokenStandVirtual) {
          if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
            console.log("🎯 Using tokenStandVirtual");
          }
          return authUserData.tokenStandVirtual;
        }

        // Para usuarios Stand Virtual, también obtener desde cookies si disponible
        if (authUserData?.isEmailUser) {
          // Intentar obtener access token desde cookies
          const cookies = document.cookie.split(";");
          for (let cookie of cookies) {
            const [name, value] = cookie.trim().split("=");
            if (name === "atbffws" && value) {
              if (
                process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development
              ) {
                console.log("🍪 Using cookie token");
              }
              return value;
            }
          }
        }
      }
    }

    if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
      console.log("❌ No token found");
    }
    return null;
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

/**
 * URLs que no requieren token de autorización
 */
const NO_AUTH_ENDPOINTS = [
  "auth/login",
  "auth/refresh-token",
  "auth/logout",
  "auth/standvirtual/login",
];

/**
 * Verificar si la URL requiere autorización
 */
const requiresAuth = (url) => {
  return !NO_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

/**
 * Obtener el número de sesión para usuarios PSS
 */
const getSessionNumber = () => {
  try {
    const persistedState = localStorage.getItem("persist:root");
    if (persistedState) {
      const parsedState = JSON.parse(persistedState);
      if (parsedState.auth) {
        const authState = JSON.parse(parsedState.auth);
        return authState?.authUserData?.numeroSesionReutilizable;
      }
    }
    return null;
  } catch (error) {
    console.error("Error getting session number:", error);
    return null;
  }
};

BaseApi.addRequestTransform((request) => {
  if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
    console.log("🔍 BaseApi Request:", request.url, request.headers);
  }

  // Solo agregar headers automáticamente si la URL requiere autenticación
  if (requiresAuth(request.url)) {
    // Debug: verificar token disponible
    const token = getAuthToken();
    const sessionNumber = getSessionNumber();

    if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
      console.log("🔑 Available token:", token ? "YES" : "NO");
      console.log("📝 Available session:", sessionNumber ? "YES" : "NO");
      console.log("⚡ Current headers:", request.headers);
    }

    // Inicializar headers si no existe
    if (!request.headers) {
      request.headers = {};
    }

    // Si ya tiene Authorization header, no sobreescribirlo
    if (!request.headers.Authorization && !request.headers.authorization) {
      if (token) {
        request.headers.Authorization = `${TOKEN_TYPE.BEARER} ${token}`;
        if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
          console.log("✅ Added Authorization header");
        }
      }
    }

    // Para usuarios PSS, agregar también número de sesión si no tiene Authorization
    if (
      !request.headers.Authorization &&
      !request.headers.authorization &&
      !request.headers["x-numero-sesion"]
    ) {
      if (sessionNumber) {
        request.headers["x-numero-sesion"] = sessionNumber;
        if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
          console.log("✅ Added session number header");
        }
      }
    }

    if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
      console.log("🔧 Final headers:", request.headers);
    }
  } else {
    if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
      console.log("⚪ Skipping auth for endpoint:", request.url);
    }
  }
});

// Agregar interceptor para manejar el caso de "unauthorized"
BaseApi.addResponseTransform((response) => {
  if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT.development) {
    console.log(response)
  }

  transformResponse(response);
});

export { BaseApi };